import type { FC, ReactNode } from 'react'
import type { Post } from '@/types'

import Link from 'next/link'

import { cn, formatDate } from '@/lib/utils'
import { AuthorByline } from '@/components/blog/author-byline'

interface PostCardProps {
    post: Post
    priority?: boolean
    className?: string
}

export const PostCard: FC<PostCardProps> = ({ post, priority = false, className }): ReactNode => {
    const href = `/${post.hubSlug}/${post.slug}`

    return (
        <article
            className={cn(
                'group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary/50',
                className
            )}
        >
            <Link href={href} className='block overflow-hidden bg-muted' tabIndex={-1} aria-hidden='true'>
                <img
                    src={post.coverImage ?? '/images/hero-smile.jpg'}
                    alt=''
                    loading={priority ? 'eager' : 'lazy'}
                    decoding='async'
                    className='aspect-[16/10] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]'
                />
            </Link>

            <div className='flex flex-1 flex-col gap-3 p-5'>
                <div className='flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary'>
                    <span>{post.hubName}</span>
                    <span className='text-border'>•</span>
                    <span className='font-medium normal-case tracking-normal text-muted-foreground'>
                        {formatDate(post.publishedAt)}
                    </span>
                </div>

                <h3 className='font-heading text-lg font-semibold leading-snug tracking-tight text-foreground'>
                    <Link href={href} className='transition-colors hover:text-primary'>
                        {post.title}
                    </Link>
                </h3>

                <p className='line-clamp-2 text-sm leading-relaxed text-muted-foreground'>
                    {post.excerpt}
                </p>

                <div className='mt-auto pt-3'>
                    <AuthorByline
                        name={post.authorName}
                        role={post.authorRole}
                        date={post.publishedAt}
                        readMinutes={post.readMinutes}
                        size='sm'
                    />
                </div>
            </div>
        </article>
    )
}
